import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { toast } from "@/components/ui/sonner";
import { Play, Pause, ExternalLink, Lock, Unlock, Disc3, ShoppingCart, Loader2, Library, Store, Settings } from "lucide-react";
import { useSearchParams } from "react-router-dom";
import { secureStorage } from "@/lib/secureStorage";
import MyCollection from "@/components/MyCollection";
import { useAuth } from "@/lib/AuthContext";

// Lemniscate parametric
const lemnX = (t, a) => { const d = 1 + Math.sin(t) ** 2; return (a * Math.cos(t)) / d; };
const lemnY = (t, a) => { const d = 1 + Math.sin(t) ** 2; return (a * Math.sin(t) * Math.cos(t)) / d; };
const lemnDepth = (t) => (1 - Math.sin(t)) / 2;

function MobiusDiscography({ albums, selectedKey, onSelect }) {
  const N = albums.length;
  const step = (2 * Math.PI) / N;
  const [offset, setOffset] = useState(0);
  const [dragging, setDragging] = useState(false);
  const [velocity, setVelocity] = useState(0);
  const dragRef = useRef({ startX: 0, startOff: 0, lastX: 0, lastT: 0 });
  const animRef = useRef(null);
  const containerRef = useRef(null);

  // Momentum
  useEffect(() => {
    if (dragging || Math.abs(velocity) < 0.001) return;
    let v = velocity;
    const tick = () => {
      v *= 0.91;
      if (Math.abs(v) < 0.001) { setVelocity(0); return; }
      setOffset(p => p + v);
      setVelocity(v);
      animRef.current = requestAnimationFrame(tick);
    };
    animRef.current = requestAnimationFrame(tick);
    return () => { if (animRef.current) cancelAnimationFrame(animRef.current); };
  }, [dragging, velocity]);

  const onTouchStart = useCallback((e) => {
    if (animRef.current) cancelAnimationFrame(animRef.current);
    setVelocity(0);
    const x = e.touches[0].clientX;
    dragRef.current = { startX: x, startOff: offset, lastX: x, lastT: Date.now() };
    setDragging(true);
  }, [offset]);

  const onTouchMove = useCallback((e) => {
    if (!dragging) return;
    const x = e.touches[0].clientX;
    const dx = x - dragRef.current.startX;
    setOffset(dragRef.current.startOff + dx * 0.005);
    const now = Date.now();
    const dt = now - dragRef.current.lastT;
    if (dt > 0) setVelocity((x - dragRef.current.lastX) * 0.005 * (16 / Math.max(dt, 1)));
    dragRef.current.lastX = x;
    dragRef.current.lastT = now;
  }, [dragging]);

  const onTouchEnd = useCallback(() => setDragging(false), []);

  const onMouseDown = useCallback((e) => {
    if (animRef.current) cancelAnimationFrame(animRef.current);
    setVelocity(0);
    dragRef.current = { startX: e.clientX, startOff: offset, lastX: e.clientX, lastT: Date.now() };
    setDragging(true);
    e.preventDefault();
  }, [offset]);

  const onMouseMove = useCallback((e) => {
    if (!dragging) return;
    const dx = e.clientX - dragRef.current.startX;
    setOffset(dragRef.current.startOff + dx * 0.005);
    const now = Date.now();
    const dt = now - dragRef.current.lastT;
    if (dt > 0) setVelocity((e.clientX - dragRef.current.lastX) * 0.005 * (16 / Math.max(dt, 1)));
    dragRef.current.lastX = e.clientX;
    dragRef.current.lastT = now;
  }, [dragging]);

  const onMouseUp = useCallback(() => setDragging(false), []);

  useEffect(() => {
    if (dragging) {
      window.addEventListener("mousemove", onMouseMove);
      window.addEventListener("mouseup", onMouseUp);
      return () => { window.removeEventListener("mousemove", onMouseMove); window.removeEventListener("mouseup", onMouseUp); };
    }
  }, [dragging, onMouseMove, onMouseUp]);

  const items = albums.map((album, i) => {
    const t = offset + i * step;
    const x = lemnX(t, 1);
    const y = lemnY(t, 1);
    const depth = lemnDepth(t);
    const scale = 0.55 + depth * 0.45;
    const opacity = 0.2 + depth * 0.8;
    const isActive = album.key === selectedKey;
    return { ...album, x, y, scale, opacity, depth, isActive, index: i, zIndex: Math.round(depth * 100) };
  });

  const sorted = [...items].sort((a, b) => a.depth - b.depth);

  // SVG lemniscate path
  const pathD = (() => {
    const pts = [];
    for (let i = 0; i <= 200; i++) {
      const t = (i / 200) * 2 * Math.PI;
      pts.push(`${i === 0 ? "M" : "L"}${lemnX(t, 1).toFixed(4)},${lemnY(t, 1).toFixed(4)}`);
    }
    return pts.join(" ") + " Z";
  })();

  return (
    <div
      ref={containerRef}
      className="relative mx-auto select-none"
      style={{ width: "100%", height: "220px", perspective: "800px", cursor: dragging ? "grabbing" : "grab" }}
      onTouchStart={onTouchStart}
      onTouchMove={onTouchMove}
      onTouchEnd={onTouchEnd}
      onMouseDown={onMouseDown}
      data-testid="mobius-strip"
    >
      {/* Lemniscate path outline */}
      <svg viewBox="-1.3 -0.7 2.6 1.4" className="absolute inset-0 w-full h-full pointer-events-none" style={{ opacity: 0.08 }}>
        <path d={pathD} fill="none" stroke="#FF0000" strokeWidth="0.025" />
      </svg>

      {/* Album cards on the strip */}
      {sorted.map((item) => {
        const pxX = 50 + item.x * 36;
        const pxY = 50 + item.y * 30;

        return (
          <div
            key={item.key}
            className="absolute transition-none"
            style={{
              left: `${pxX}%`,
              top: `${pxY}%`,
              transform: `translate(-50%, -50%) scale(${item.scale})`,
              opacity: item.opacity,
              zIndex: item.zIndex,
              pointerEvents: item.depth > 0.3 ? "auto" : "none",
            }}
            onClick={(e) => {
              const dx = Math.abs(e.clientX - dragRef.current.startX);
              if (dx < 8) onSelect(item.key, item.index);
            }}
            data-testid={`mobius-album-${item.key}`}
          >
            <div className={`flex items-center gap-2 p-2 sm:p-3 border font-mono transition-colors duration-150 min-w-[140px] sm:min-w-[180px] ${
              item.isActive
                ? "border-[#FF0000]/70 bg-[#FF0000]/10 shadow-[0_0_14px_rgba(255,0,0,0.2)]"
                : "border-[#FF0000]/20 bg-black/90 hover:border-[#FF0000]/40"
            }`}>
              {item.cover_art_url && (
                <img
                  src={item.cover_art_url}
                  alt={item.title}
                  className="w-10 h-10 sm:w-12 sm:h-12 object-cover shrink-0 border border-[#FF0000]/20"
                  draggable={false}
                />
              )}
              <div className="min-w-0">
                <div className={`text-xs sm:text-sm font-bold tracking-wide truncate ${item.isActive ? "text-[#FF0000]" : "text-[#FF0000]/80"}`}
                  style={item.isActive ? { textShadow: '0 0 8px rgba(255,0,0,0.3)' } : {}}>
                  {item.title}
                </div>
                <div className="text-[8px] sm:text-[9px] tracking-widest text-[#FF0000]/40 uppercase truncate">
                  {item.artist}
                </div>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

const API = process.env.REACT_APP_BACKEND_URL ? `${process.env.REACT_APP_BACKEND_URL}/api` : "/api";
const MIRRORS_ID_KEY = "mirrors_id";

const ALBUM_PACKAGE_MAP = {
  "lucid-dreams": "album_lucid_dreams",
  "13": "album_13",
  "presence": "album_presence",
  "in-memory-of": "album_in_memory_of",
  "codes": "album_codes",
  "mirrors": "album_mirrors",
};

const VINCE_MAIN = "https://www.vincesinnott.com";
const LUCID_DREAMS_COVER_URL =
  "https://customer-assets.emergentagent.com/job_3a6acb3e-074e-41f3-a0be-676978ae6151/artifacts/37hk2mpj_Lucid%20Dreams%20Album%20Cover.jpg";
const PRESENCE_COVER_URL =
  "https://customer-assets.emergentagent.com/job_3a6acb3e-074e-41f3-a0be-676978ae6151/artifacts/a24t5yb5_Presence%20Album%20Cover.jpeg";
const THIRTEEN_COVER_URL =
  "https://customer-assets.emergentagent.com/job_3a6acb3e-074e-41f3-a0be-676978ae6151/artifacts/zqmer3ut_%2213%22%20Cover%20Art.jpg";
const IN_MEMORY_OF_COVER_URL =
  "https://customer-assets.emergentagent.com/job_3a6acb3e-074e-41f3-a0be-676978ae6151/artifacts/0naz4521_%22In%20Memory%20Of%22%20Cover%20Art.jpeg";
const MIRRORS_LOGO_URL =
  "https://customer-assets.emergentagent.com/job_3a6acb3e-074e-41f3-a0be-676978ae6151/artifacts/nghiht67_MIRRORS_1024x1024.jpg";
const CODES_COVER_URL =
  "https://customer-assets.emergentagent.com/job_3a6acb3e-074e-41f3-a0be-676978ae6151/artifacts/v6sfsrhe_CODES%20Album%20Cover.jpg";
const APPLE_CODES_URL = "https://music.apple.com/us/album/codes/1812232122";
const APPLE_CODES_ID = "1812232122";

// Mirrors album tracks (full songs — hosted MP3s)
const MIRRORS_TRACKS = [
  {
    id: "mirrors-1",
    track_number: 1,
    title: "13th St",
    camelot: "6A",
    bpm: 113,
    audio_url: "https://customer-assets.emergentagent.com/job_3a6acb3e-074e-41f3-a0be-676978ae6151/artifacts/ehvrkk6h_6A%20-%20113%20-%2013th%20St.mp3",
  },
  {
    id: "mirrors-2",
    track_number: 2,
    title: "96 MIRRORS",
    camelot: "8A",
    bpm: 120,
    audio_url: "https://customer-assets.emergentagent.com/job_3a6acb3e-074e-41f3-a0be-676978ae6151/artifacts/xd54fjdg_8A%20-%20120%20-%2096%20MIRRORS.mp3",
  },
  {
    id: "mirrors-3",
    track_number: 3,
    title: "WENOTDONE",
    camelot: "12A",
    bpm: 120,
    audio_url: "https://customer-assets.emergentagent.com/job_3a6acb3e-074e-41f3-a0be-676978ae6151/artifacts/umcbdu9h_12A%20-%20120%20-%20WENOTDONE.mp3",
  },
  {
    id: "mirrors-4",
    track_number: 4,
    title: "$hango 1",
    camelot: "9B",
    bpm: 96,
    audio_url: "https://customer-assets.emergentagent.com/job_3a6acb3e-074e-41f3-a0be-676978ae6151/artifacts/y2ywa1ob_9B%20-%2096%20-%20%24hango%201.mp3",
  },
  {
    id: "mirrors-5",
    track_number: 5,
    title: "Musical Chairs 2026",
    audio_url: "https://customer-assets.emergentagent.com/job_3a6acb3e-074e-41f3-a0be-676978ae6151/artifacts/nuv46s0x_Musical%20Chairs%202026.wav",
  },
  {
    id: "mirrors-6",
    track_number: 6,
    title: "Spun The Block",
    audio_url: "https://customer-assets.emergentagent.com/job_3a6acb3e-074e-41f3-a0be-676978ae6151/artifacts/ctvnadiy_Spun%20The%20Block.mp3",
  },
  {
    id: "mirrors-7",
    track_number: 7,
    title: "Here 2day Gone 2morrow",
    camelot: "7B",
    bpm: 126,
    audio_url: "https://customer-assets.emergentagent.com/job_3a6acb3e-074e-41f3-a0be-676978ae6151/artifacts/ki6mg2o7_7B%20-%20126%20-%20Here%202day%20Gone%202morrow%201.0%20Master.mp3",
  },
];

// Album "13" tracks
const THIRTEEN_TRACKS = [
  {
    id: "13-1",
    track_number: 1,
    title: "13Elieve",
    audio_url: "https://customer-assets.emergentagent.com/job_3a6acb3e-074e-41f3-a0be-676978ae6151/artifacts/e3bmyvap_13Elieve.m4a",
  },
  {
    id: "13-2",
    track_number: 2,
    title: "Homme Sweet Home",
    audio_url: "https://customer-assets.emergentagent.com/job_3a6acb3e-074e-41f3-a0be-676978ae6151/artifacts/asrjwzoa_Homme%20Sweet%20Home.m4a",
  },
  {
    id: "13-3",
    track_number: 3,
    title: "Leo's House",
    audio_url: "https://customer-assets.emergentagent.com/job_3a6acb3e-074e-41f3-a0be-676978ae6151/artifacts/hf6ijlup_Leo%27s%20House.m4a",
  },
  {
    id: "13-4",
    track_number: 4,
    title: "Lost",
    audio_url: "https://customer-assets.emergentagent.com/job_3a6acb3e-074e-41f3-a0be-676978ae6151/artifacts/ivufjxie_Lost.m4a",
  },
  {
    id: "13-5",
    track_number: 5,
    title: "The 2nd Coming (feat. Boogie Dash)",
    audio_url: "https://customer-assets.emergentagent.com/job_3a6acb3e-074e-41f3-a0be-676978ae6151/artifacts/vf45z7fx_The%202nd%20Coming%20%28feat.%20Boogie%20Dash%29.m4a",
  },
];

// Lucid Dreams album tracks
const LUCID_DREAMS_TRACKS = [
  {
    id: "lucid-1",
    track_number: 1,
    title: "Lucid Dreams",
    audio_url: "https://customer-assets.emergentagent.com/job_3a6acb3e-074e-41f3-a0be-676978ae6151/artifacts/k0bx6olb_Lucid%20Dreams.m4a",
  },
  {
    id: "lucid-2",
    track_number: 2,
    title: "New Waves",
    audio_url: "https://customer-assets.emergentagent.com/job_3a6acb3e-074e-41f3-a0be-676978ae6151/artifacts/oyfkic7r_New%20Waves.m4a",
  },
  {
    id: "lucid-3",
    track_number: 3,
    title: "Play Rough (feat. Boogie Dash)",
    audio_url: "https://customer-assets.emergentagent.com/job_3a6acb3e-074e-41f3-a0be-676978ae6151/artifacts/08436du3_Play%20Rough%28feat%20Boogie%20Dash%29.m4a",
  },
  {
    id: "lucid-4",
    track_number: 4,
    title: "Worry Free",
    audio_url: "https://customer-assets.emergentagent.com/job_3a6acb3e-074e-41f3-a0be-676978ae6151/artifacts/qqgseck7_Worry%20Free.m4a",
  },
];

const FEATURED_ALBUMS = [
  {
    key: "lucid-dreams",
    title: "Lucid Dreams",
    artist: "VINCE SINNOTT",
    label: "GODMODE SOUND",
    buyUrl: VINCE_MAIN,
    streamUrl: VINCE_MAIN,
    cover_art_url: LUCID_DREAMS_COVER_URL,
    tracks: LUCID_DREAMS_TRACKS,
  },
  {
    key: "13",
    title: "13",
    artist: "VINCE SINNOTT",
    label: "GODMODE SOUND",
    buyUrl: VINCE_MAIN,
    streamUrl: VINCE_MAIN,
    cover_art_url: THIRTEEN_COVER_URL,
    tracks: THIRTEEN_TRACKS,
  },
  {
    key: "presence",
    title: "Presence",
    artist: "VINCE SINNOTT",
    label: "GODMODE SOUND",
    buyUrl: VINCE_MAIN,
    streamUrl: VINCE_MAIN,
    cover_art_url: PRESENCE_COVER_URL,
    tracks: [
      { id: "pres-1", track_number: 1, title: "Still N.Y.C", camelot: "9A", bpm: 90, audio_url: "https://customer-assets.emergentagent.com/job_3a6acb3e-074e-41f3-a0be-676978ae6151/artifacts/u7nbzcl5_9A%20-%2090%20-%20Still%20N.Y.C.m4a" },
      { id: "pres-2", track_number: 2, title: "Etheric Template", audio_url: "https://customer-assets.emergentagent.com/job_3a6acb3e-074e-41f3-a0be-676978ae6151/artifacts/hgz4viax_Etheric%20Template.m4a" },
      { id: "pres-3", track_number: 3, title: "Le Vision", audio_url: "https://customer-assets.emergentagent.com/job_3a6acb3e-074e-41f3-a0be-676978ae6151/artifacts/cwusivq6_Le%20Vision.m4a" },
      { id: "pres-4", track_number: 4, title: "Leo's House", audio_url: "https://customer-assets.emergentagent.com/job_3a6acb3e-074e-41f3-a0be-676978ae6151/artifacts/s30jpcxo_Leo%27s%20House.m4a" },
      { id: "pres-5", track_number: 5, title: "1990 6", audio_url: "https://customer-assets.emergentagent.com/job_3a6acb3e-074e-41f3-a0be-676978ae6151/artifacts/7w67b8gl_1990%206.mp3" },
      { id: "pres-6", track_number: 6, title: "Feel Like Summer (Vmix)", audio_url: "https://customer-assets.emergentagent.com/job_3a6acb3e-074e-41f3-a0be-676978ae6151/artifacts/czql7f6q_Feel%20Like%20Summer%20%28Vmix%29.mp3" },
    ],
  },
  {
    key: "in-memory-of",
    title: "In Memory Of",
    artist: "VINCE SINNOTT",
    label: "GODMODE SOUND",
    buyUrl: "https://music.apple.com/us/album/in-memory-of/1340448168",
    streamUrl: "https://music.apple.com/us/album/in-memory-of/1340448168",
    cover_art_url: IN_MEMORY_OF_COVER_URL,
    itunesAlbumId: "1340448168",
  },
  {
    key: "codes",
    title: "CODES",
    artist: "VINCE SINNOTT",
    label: "GODMODE SOUND",
    buyUrl: APPLE_CODES_URL,
    streamUrl: APPLE_CODES_URL,
    itunesAlbumId: APPLE_CODES_ID,
    cover_art_url: CODES_COVER_URL,
  },
  {
    key: "mirrors",
    title: "Mirrors",
    artist: "VINCE SINNOTT",
    label: "GODMODE SOUND",
    buyUrl: VINCE_MAIN,
    streamUrl: VINCE_MAIN,
    cover_art_url: MIRRORS_LOGO_URL,
    tracks: MIRRORS_TRACKS,
  },
];

function formatMs(ms) {
  const totalSeconds = Math.floor((ms || 0) / 1000);
  const m = Math.floor(totalSeconds / 60);
  const s = totalSeconds % 60;
  return `${m}:${String(s).padStart(2, "0")}`;
}

export default function SoundPage() {
  const { user } = useAuth();
  const [mirrorsId, setMirrorsId] = useState("");
  const [savedMirrorsId, setSavedMirrorsId] = useState("");

  const [activeTab, setActiveTab] = useState("store");
  const [collectionAlbums, setCollectionAlbums] = useState([]);
  const [collectionLoading, setCollectionLoading] = useState(false);

  const [activeIndex, setActiveIndex] = useState(4);
  const [selectedAlbumKey, setSelectedAlbumKey] = useState("codes");

  const [isLoadingTracks, setIsLoadingTracks] = useState(false);
  const [itunesTracks, setItunesTracks] = useState([]);
  const [buyingId, setBuyingId] = useState(null);
  const [dbAlbums, setDbAlbums] = useState([]);
  const [searchParams] = useSearchParams();

  const audioRef = useRef(null);
  const manualHoldUntilRef = useRef(0);
  const [playingTrackId, setPlayingTrackId] = useState(null);
  const [isPaused, setIsPaused] = useState(false);
  const [trackProgress, setTrackProgress] = useState(0);
  const [trackDuration, setTrackDuration] = useState(0);
  const progressInterval = useRef(null);

  const [atmosEnabled, setAtmosEnabled] = useState(false);
  const atmosRef = useRef({ ctx: null, source: null });

  const setupAtmos = (audioEl) => {
    try {
      const ctx = new (window.AudioContext || window.webkitAudioContext)();
      const source = ctx.createMediaElementSource(audioEl);
      audioEl.crossOrigin = "anonymous";

      // SUB BASS → below/behind
      const subLP = ctx.createBiquadFilter();
      subLP.type = "lowpass"; subLP.frequency.value = 120;
      const subP = ctx.createPanner();
      subP.panningModel = "HRTF"; subP.positionX.value = 0; subP.positionY.value = -0.8; subP.positionZ.value = -0.5;
      source.connect(subLP); subLP.connect(subP); subP.connect(ctx.destination);

      // LOW MIDS → left/right wide
      const lowBP = ctx.createBiquadFilter();
      lowBP.type = "bandpass"; lowBP.frequency.value = 300; lowBP.Q.value = 0.7;
      const lP = ctx.createPanner();
      lP.panningModel = "HRTF"; lP.positionX.value = -1.2; lP.positionY.value = 0.2; lP.positionZ.value = 0.3;
      const rP = ctx.createPanner();
      rP.panningModel = "HRTF"; rP.positionX.value = 1.2; rP.positionY.value = 0.2; rP.positionZ.value = 0.3;
      const lowG = ctx.createGain(); lowG.gain.value = 0.8;
      source.connect(lowBP); lowBP.connect(lowG); lowG.connect(lP); lowG.connect(rP);
      lP.connect(ctx.destination); rP.connect(ctx.destination);

      // MIDS → front center (vocals)
      const midBP = ctx.createBiquadFilter();
      midBP.type = "bandpass"; midBP.frequency.value = 1200; midBP.Q.value = 0.5;
      const fP = ctx.createPanner();
      fP.panningModel = "HRTF"; fP.positionX.value = 0; fP.positionY.value = 0.3; fP.positionZ.value = 1;
      source.connect(midBP); midBP.connect(fP); fP.connect(ctx.destination);

      // HIGH MIDS → rear sides
      const highBP = ctx.createBiquadFilter();
      highBP.type = "bandpass"; highBP.frequency.value = 4000; highBP.Q.value = 0.5;
      const rlP = ctx.createPanner();
      rlP.panningModel = "HRTF"; rlP.positionX.value = -0.8; rlP.positionY.value = 0.5; rlP.positionZ.value = -0.8;
      const rrP = ctx.createPanner();
      rrP.panningModel = "HRTF"; rrP.positionX.value = 0.8; rrP.positionY.value = 0.5; rrP.positionZ.value = -0.8;
      const highG = ctx.createGain(); highG.gain.value = 0.6;
      source.connect(highBP); highBP.connect(highG); highG.connect(rlP); highG.connect(rrP);
      rlP.connect(ctx.destination); rrP.connect(ctx.destination);

      // AIR/PRESENCE → above
      const airHP = ctx.createBiquadFilter();
      airHP.type = "highpass"; airHP.frequency.value = 6000;
      const tP = ctx.createPanner();
      tP.panningModel = "HRTF"; tP.positionX.value = 0; tP.positionY.value = 1.5; tP.positionZ.value = 0;
      const airG = ctx.createGain(); airG.gain.value = 0.5;
      source.connect(airHP); airHP.connect(airG); airG.connect(tP); tP.connect(ctx.destination);

      atmosRef.current = { ctx, source };
    } catch (e) {
      console.warn("ATMOS setup failed:", e);
    }
  };

  const cleanupAtmos = () => {
    if (atmosRef.current.ctx) {
      try { atmosRef.current.ctx.close(); } catch {}
      atmosRef.current = { ctx: null, source: null };
    }
  };

  const isUnlocked = !!savedMirrorsId;

  const fetchCollection = useCallback(async (mid) => {
    if (!mid) { setCollectionAlbums([]); return; }
    setCollectionLoading(true);
    try {
      const res = await axios.get(`${API}/collection/${mid}`);
      setCollectionAlbums(res.data?.albums || []);
    } catch {
      setCollectionAlbums([]);
    } finally {
      setCollectionLoading(false);
    }
  }, []);

  useEffect(() => {
    const stored = secureStorage.getItem(MIRRORS_ID_KEY) || "";
    setSavedMirrorsId(stored);
    setMirrorsId(stored);
    if (stored) fetchCollection(stored);
  }, [fetchCollection]);

  // Fetch DB-created albums (from admin discography UI)
  useEffect(() => {
    axios
      .get(`${API}/discography`)
      .then((res) => setDbAlbums(res.data?.albums || []))
      .catch(() => setDbAlbums([]));
  }, []);

  // Handle Stripe checkout return — add purchased album to collection only.
  // Subscription activation (toast + URL cleanup + polling) is handled
  // centrally in AuthContext to avoid double-firing.
  useEffect(() => {
    const status = searchParams.get("checkout");
    const sessionId = searchParams.get("session_id");
    if (status !== "success" || !sessionId) return;
    const mid = secureStorage.getItem(MIRRORS_ID_KEY) || "";
    if (!mid) return;
    axios.post(`${API}/collection/verify`, { session_id: sessionId, mirrors_id: mid })
      .then(() => {
        fetchCollection(mid);
        setActiveTab("collection");
      })
      .catch(() => {});
  }, [searchParams, fetchCollection]);

  const buyViaStripe = async (packageId) => {
    setBuyingId(packageId);
    const maxRetries = 2;
    for (let attempt = 0; attempt <= maxRetries; attempt++) {
      try {
        const res = await axios.post(`${API}/payments/v1/checkout/session`, {
          package_id: packageId,
          origin_url: window.location.origin,
          return_path: "/sound",
          mirrors_id: savedMirrorsId || "",
        });
        if (res.data?.url) {
          window.location.href = res.data.url;
          return;
        }
        toast.error("Could not create checkout session");
        setBuyingId(null);
        return;
      } catch (e) {
        const status = e.response?.status;
        const detail = e.response?.data?.detail;
        if (attempt < maxRetries && (!status || status >= 500)) {
          await new Promise(r => setTimeout(r, 800 * (attempt + 1)));
          continue;
        }
        console.error("Stripe checkout error:", e);
        toast.error(detail || (status === 400 ? "Invalid package selected" : "Checkout failed — please try again"));
        setBuyingId(null);
        return;
      }
    }
    setBuyingId(null);
  };

  // Auto-advance carousel (pauses for ~12s after any manual selection)
  useEffect(() => {
    const t = setInterval(() => {
      if (Date.now() < manualHoldUntilRef.current) return;
      setActiveIndex((i) => {
        const next = (i + 1) % FEATURED_ALBUMS.length;
        setSelectedAlbumKey(FEATURED_ALBUMS[next].key);
        return next;
      });
    }, 4200);

    return () => clearInterval(t);
  }, []);

  const selectedAlbum = useMemo(
    () => FEATURED_ALBUMS.find((a) => a.key === selectedAlbumKey) || FEATURED_ALBUMS[0],
    [selectedAlbumKey]
  );

  useEffect(() => {
    if (selectedAlbum?.itunesAlbumId) {
      void loadItunesTracks(selectedAlbum.itunesAlbumId);
    } else {
      setItunesTracks([]);
    }
  }, [selectedAlbum?.key]);

  const loadItunesTracks = async (albumId) => {
    setIsLoadingTracks(true);
    try {
      const res = await axios.get(`${API}/itunes/album/${albumId}`);
      setItunesTracks(res.data?.tracks || []);
    } catch (e) {
      setItunesTracks([]);
    } finally {
      setIsLoadingTracks(false);
    }
  };

  const saveId = () => {
    const clean = (mirrorsId || "").trim();
    if (!clean) {
      toast.error("Enter a MIRRORS ID");
      return;
    }
    secureStorage.setItem(MIRRORS_ID_KEY, clean);
    setSavedMirrorsId(clean);
    fetchCollection(clean);
    toast.success("MIRRORS ID saved — full access unlocked");
  };

  const clearId = () => {
    secureStorage.removeItem(MIRRORS_ID_KEY);
    setSavedMirrorsId("");
    setMirrorsId("");
    setCollectionAlbums([]);
    toast.message("MIRRORS ID cleared");
  };

  const stopAudio = () => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
    }
    if (progressInterval.current) clearInterval(progressInterval.current);
    cleanupAtmos();
    setPlayingTrackId(null);
    setIsPaused(false);
    setTrackProgress(0);
    setTrackDuration(0);
  };

  const playFullTrack = (track) => {
    if (!track?.audio_url) return;

    if (playingTrackId === track.id && !isPaused) {
      audioRef.current?.pause();
      setIsPaused(true);
      if (progressInterval.current) clearInterval(progressInterval.current);
      return;
    }
    if (playingTrackId === track.id && isPaused) {
      audioRef.current?.play();
      setIsPaused(false);
      startProgressTracking();
      return;
    }

    stopAudio();
    const audio = new Audio(track.audio_url);
    if (atmosEnabled) audio.crossOrigin = "anonymous";
    audioRef.current = audio;
    audio.volume = atmosEnabled ? 1.0 : 0.8;
    if (atmosEnabled) setupAtmos(audio);
    audio.play();
    setPlayingTrackId(track.id);
    setIsPaused(false);

    audio.addEventListener("loadedmetadata", () => {
      setTrackDuration(audio.duration);
    });
    audio.addEventListener("ended", () => {
      setPlayingTrackId(null);
      setIsPaused(false);
      setTrackProgress(0);
      if (progressInterval.current) clearInterval(progressInterval.current);
    });
    startProgressTracking();
  };

  const startProgressTracking = () => {
    if (progressInterval.current) clearInterval(progressInterval.current);
    progressInterval.current = setInterval(() => {
      if (audioRef.current) {
        setTrackProgress(audioRef.current.currentTime);
        setTrackDuration(audioRef.current.duration || 0);
      }
    }, 250);
  };

  const seekTrack = (e, track) => {
    if (!audioRef.current || playingTrackId !== track.id) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const pct = (e.clientX - rect.left) / rect.width;
    audioRef.current.currentTime = pct * (audioRef.current.duration || 0);
  };

  const formatSec = (s) => {
    if (!s || isNaN(s)) return "0:00";
    const m = Math.floor(s / 60);
    const sec = Math.floor(s % 60);
    return `${m}:${String(sec).padStart(2, "0")}`;
  };

  const playPreview = (track) => {
    if (!track?.preview_url) return;

    // If switching tracks, stop the previous one.
    if (playingTrackId && playingTrackId !== track.track_id) {
      stopAudio();
    }

    // Create or reuse audio.
    if (!audioRef.current) {
      audioRef.current = new Audio();
      if (atmosEnabled) audioRef.current.crossOrigin = "anonymous";
      audioRef.current.addEventListener("ended", () => {
        setPlayingTrackId(null);
        setIsPaused(false);
      });
      audioRef.current.addEventListener("timeupdate", () => {
        // Preview gate: hard stop at 30s if not unlocked.
        if (!isUnlocked && audioRef.current && audioRef.current.currentTime >= 30) {
          stopAudio();
          toast.message("Preview ended — enter your MIRRORS ID to unlock full access links");
        }
      });
    }

    // Toggle pause/play on same track.
    if (playingTrackId === track.track_id) {
      if (audioRef.current.paused) {
        audioRef.current.play();
        setIsPaused(false);
      } else {
        audioRef.current.pause();
        setIsPaused(true);
      }
      return;
    }

    audioRef.current.src = track.preview_url;
    audioRef.current.currentTime = 0;
    audioRef.current.play();
    setPlayingTrackId(track.track_id);
    setIsPaused(false);
  };

  const albumCardStyle = (idx) => {
    const total = FEATURED_ALBUMS.length;
    const offset = ((idx - activeIndex) % total + total) % total;
    const centeredOffset = offset > total / 2 ? offset - total : offset; // [-3..3]

    const rotate = centeredOffset * 18;
    const translateZ = 240 - Math.abs(centeredOffset) * 55;
    const translateX = centeredOffset * 110;
    const opacity = Math.max(0.2, 1 - Math.abs(centeredOffset) * 0.18);
    const scale = 1 - Math.abs(centeredOffset) * 0.08;

    return {
      transform: `translateX(${translateX}px) translateZ(${translateZ}px) rotateY(${rotate}deg) scale(${scale})`,
      opacity,
      zIndex: 100 - Math.abs(centeredOffset),
    };
  };

  return (
    <div
      className="min-h-screen sound-red"
      style={{
        background:
          "radial-gradient(1200px 600px at 50% 0%, rgba(255, 23, 68, 0.25), rgba(5,5,5,1) 55%), linear-gradient(180deg, rgba(255, 23, 68, 0.18), rgba(5,5,5,1) 55%)",
      }}
    >
      <div className="mx-auto max-w-6xl px-4 sm:px-6 py-8">
        <div className="flex flex-col gap-4">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div>
              <div className="flex items-center gap-3">
                <Disc3 className="h-6 w-6 text-[#FF1744]" />
                <h1
                  className="text-4xl sm:text-5xl font-bold tracking-tight"
                  style={{ color: "#FF1744", textShadow: "0 0 18px rgba(255, 23, 68, 0.35)" }}
                >
                  SOUND
                </h1>
              </div>
              <p className="mt-2 text-sm sm:text-base" style={{ color: "rgba(255, 23, 68, 0.85)" }}>
                GODMODE SOUND discography — preview inside the grid. Unlock full access with your MIRRORS ID.
              </p>
            </div>

            <div className="flex items-center gap-2">
                {user?.role === "admin" && (
                <Link
                  to="/admin/discography"
                  className="px-3 py-2 text-xs font-mono uppercase tracking-widest border border-[#FF1744]/60 text-[#FF1744] hover:bg-[#FF1744]/10 rounded inline-flex items-center gap-1.5"
                  data-testid="admin-discography-link"
                >
                  <Settings className="h-3 w-3" />
                  Admin
                </Link>
                )}
                {activeTab === "store" && (
                <Button
                  variant="outline"
                  className="border-[#FF1744]/60 text-[#FF1744] hover:bg-[#FF1744]/10"
                  onClick={() => buyViaStripe("discography")}
                  disabled={buyingId === "discography"}
                  data-testid="buy-discography-btn"
                >
                  {buyingId === "discography" ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <ShoppingCart className="h-4 w-4 mr-2" />
                  )}
                  Buy Discography — $44.96
                </Button>
                )}
            </div>
          </div>

          {/* Tab Switcher */}
          <div className="flex items-center gap-1 p-1 bg-black/80 border border-[#FF0000]/20" data-testid="sound-tabs">
            <button
              onClick={() => setActiveTab("store")}
              className={`flex-1 flex items-center justify-center gap-2 py-2.5 px-4 text-xs tracking-widest uppercase font-mono transition-all ${
                activeTab === "store"
                  ? "bg-[#FF0000]/15 text-[#FF0000] border border-[#FF0000]/40 shadow-[0_0_12px_rgba(255,0,0,0.15)]"
                  : "text-[#FF0000]/40 hover:text-[#FF0000]/70 border border-transparent"
              }`}
              data-testid="tab-store"
            >
              <Store className="h-3.5 w-3.5" />
              STORE
            </button>
            <button
              onClick={() => { setActiveTab("collection"); if (savedMirrorsId) fetchCollection(savedMirrorsId); }}
              className={`flex-1 flex items-center justify-center gap-2 py-2.5 px-4 text-xs tracking-widest uppercase font-mono transition-all ${
                activeTab === "collection"
                  ? "bg-[#FF0000]/15 text-[#FF0000] border border-[#FF0000]/40 shadow-[0_0_12px_rgba(255,0,0,0.15)]"
                  : "text-[#FF0000]/40 hover:text-[#FF0000]/70 border border-transparent"
              }`}
              data-testid="tab-collection"
            >
              <Library className="h-3.5 w-3.5" />
              MY COLLECTION
              {collectionAlbums.length > 0 && (
                <span className="ml-1 px-1.5 py-0.5 text-[9px] bg-[#FF0000]/20 border border-[#FF0000]/30 rounded-sm">
                  {collectionAlbums.length}
                </span>
              )}
            </button>
          </div>

          {/* COLLECTION TAB */}
          {activeTab === "collection" && (
            <>
              {!savedMirrorsId ? (
                <Card className="bg-[#0B0004]/70 border-[#FF0000]/20">
                  <CardContent className="p-8 text-center">
                    <Lock className="h-10 w-10 mx-auto mb-3" style={{ color: "rgba(255,0,0,0.3)" }} />
                    <div className="text-base font-bold font-mono" style={{ color: "rgba(255,0,0,0.7)" }}>
                      ENTER YOUR MIRRORS ID
                    </div>
                    <div className="text-sm mt-2 font-mono" style={{ color: "rgba(255,0,0,0.4)" }}>
                      Your MIRRORS ID links purchases to your collection. Enter it below or switch to the STORE tab.
                    </div>
                    <div className="flex items-center justify-center gap-2 mt-4">
                      <Input
                        value={mirrorsId}
                        onChange={(e) => setMirrorsId(e.target.value)}
                        placeholder="Enter MIRRORS ID"
                        className="w-[220px] bg-[#050505]/70 border-[#FF0000]/40 text-[#FF0000] placeholder:text-[#FF0000]/40"
                      />
                      <Button onClick={saveId} className="bg-[#FF0000] text-black hover:bg-[#FF0000]/90 font-mono">
                        Save
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ) : collectionLoading ? (
                <div className="flex items-center justify-center py-16">
                  <Loader2 className="h-6 w-6 animate-spin" style={{ color: "#FF0000" }} />
                  <span className="ml-3 text-sm font-mono" style={{ color: "rgba(255,0,0,0.6)" }}>Loading collection...</span>
                </div>
              ) : (
                <MyCollection
                  albums={collectionAlbums}
                  mirrorsId={savedMirrorsId}
                  onRefresh={() => fetchCollection(savedMirrorsId)}
                  atmosEnabled={atmosEnabled}
                  setupAtmos={setupAtmos}
                  cleanupAtmos={cleanupAtmos}
                />
              )}
            </>
          )}

          {/* STORE TAB */}
          {activeTab === "store" && (
          <>
          <Card className="bg-[#0B0004]/70 border-[#FF1744]/30">
            <CardContent className="p-4 sm:p-5">
              <div className="flex items-center justify-between gap-4 flex-wrap">
                <div className="flex items-center gap-2">
                  {isUnlocked ? (
                    <Badge className="bg-[#FF1744]/20 text-[#FF1744] border border-[#FF1744]/40">
                      <Unlock className="h-3 w-3 mr-1" /> MIRRORS ID ACTIVE
                    </Badge>
                  ) : (
                    <Badge className="bg-[#FF1744]/10 text-[#FF1744] border border-[#FF1744]/30">
                      <Lock className="h-3 w-3 mr-1" /> PREVIEW MODE (30s)
                    </Badge>
                  )}

                  {!isUnlocked && (
                    <Link to="/mirrors" className="text-xs underline" style={{ color: "rgba(255, 23, 68, 0.85)" }}>
                      Create a MIRRORS ID
                    </Link>
                  )}
                </div>

                <div className="flex items-center gap-2 flex-wrap">
                  <Input
                    value={mirrorsId}
                    onChange={(e) => setMirrorsId(e.target.value)}
                    placeholder="Enter MIRRORS ID"
                    className="w-[220px] bg-[#050505]/70 border-[#FF1744]/40 text-[#FF1744] placeholder:text-[#FF1744]/50"
                  />
                  <Button
                    onClick={saveId}
                    className="bg-[#FF1744] text-black hover:bg-[#FF1744]/90"
                  >
                    Save
                  </Button>
                  <Button
                    variant="outline"
                    onClick={clearId}
                    className="border-[#FF1744]/60 text-[#FF1744] hover:bg-[#FF1744]/10"
                  >
                    Clear
                  </Button>
                </div>
              </div>

              <div className="mt-3 text-xs" style={{ color: "rgba(255, 23, 68, 0.75)" }}>
                Full songs are external: Apple Music + vincesinnott.com purchase links. Previews play inside the app.
              </div>
            </CardContent>
          </Card>

          {/* Mobius Strip Discography */}
          <div className="bg-black border border-[#FF0000]/15 p-4" data-testid="mobius-discography">
            <div className="text-[10px] tracking-widest uppercase text-[#FF0000]/60 mb-2 font-mono">&gt; Discography — Swipe to browse</div>
            <MobiusDiscography
              albums={FEATURED_ALBUMS}
              selectedKey={selectedAlbumKey}
              onSelect={(key, idx) => {
                manualHoldUntilRef.current = Date.now() + 12000;
                setSelectedAlbumKey(key);
                setActiveIndex(idx);
              }}
            />
          </div>

          {/* DB-uploaded albums (admin) */}
          {dbAlbums.length > 0 && (
            <div className="bg-black border border-[#FF0000]/15 p-4" data-testid="db-albums-section">
              <div className="text-[10px] tracking-widest uppercase text-[#FF0000]/60 mb-3 font-mono">
                &gt; New Releases ({dbAlbums.length})
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                {dbAlbums.map((a) => (
                  <div
                    key={a.id}
                    className="border border-[#FF0000]/20 bg-[#0B0004]/70 p-2 hover:border-[#FF0000]/50 transition-colors"
                    data-testid={`db-album-${a.id}`}
                  >
                    {a.cover_art_url ? (
                      <img
                        src={a.cover_art_url}
                        alt={a.title}
                        className="w-full aspect-square object-cover border border-[#FF0000]/20"
                      />
                    ) : (
                      <div className="w-full aspect-square border border-[#FF0000]/20 flex items-center justify-center bg-[#FF0000]/3">
                        <Disc3 className="w-8 h-8 text-[#FF0000]/30" />
                      </div>
                    )}
                    <div className="mt-2 text-sm font-bold text-[#FF0000] truncate">{a.title}</div>
                    <div className="text-[9px] font-mono text-[#FF0000]/50 truncate">{a.artist}</div>
                    <div className="text-[8px] font-mono text-[#FF0000]/30 mt-0.5">
                      {(a.tracks?.length || 0)} tracks · {a.status}
                    </div>
                    {a.buy_url && (
                      <a
                        href={a.buy_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="mt-2 inline-flex items-center gap-1 text-[10px] font-mono text-[#FF0000]/80 hover:text-[#FF0000] underline underline-offset-2"
                      >
                        <ExternalLink className="w-3 h-3" /> Buy
                      </a>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          <Card className="bg-[#0B0004]/70 border-[#FF1744]/30">
            <CardContent className="p-5">
              <div className="flex items-start justify-between gap-4 flex-wrap">
                <div>
                  <div className="text-xs" style={{ color: "rgba(255, 23, 68, 0.7)" }}>
                    SELECTED
                  </div>
                  <div className="flex items-center gap-3">
                    {selectedAlbum.cover_art_url ? (
                      <img
                        src={selectedAlbum.cover_art_url}
                        alt={`${selectedAlbum.title} cover art`}
                        className="h-10 w-10 rounded-md object-cover"
                        style={{ boxShadow: "0 0 18px rgba(255, 23, 68, 0.25)", border: "1px solid rgba(255, 23, 68, 0.35)" }}
                      />
                    ) : (
                      <div
                        className="h-10 w-10 rounded-md flex items-center justify-center"
                        style={{ border: "1px solid rgba(255, 23, 68, 0.25)", background: "rgba(255, 23, 68, 0.06)" }}
                      >
                        <Disc3 className="h-5 w-5" style={{ color: "rgba(255, 23, 68, 0.85)" }} />
                      </div>
                    )}

                    <div className="text-2xl font-bold" style={{ color: "#FF1744" }}>
                      {selectedAlbum.title}
                    </div>
                  </div>
                  <div className="text-sm" style={{ color: "rgba(255, 23, 68, 0.75)" }}>
                    {selectedAlbum.artist} <span style={{ color: "rgba(255, 23, 68, 0.55)" }}>•</span> {selectedAlbum.label}
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setAtmosEnabled(p => !p)}
                    className={`px-3 py-2 text-[9px] tracking-widest uppercase font-mono border transition-all ${
                      atmosEnabled
                        ? "border-[#FF0000] bg-[#FF0000]/20 text-[#FF0000] shadow-[0_0_12px_rgba(255,0,0,0.25)]"
                        : "border-[#FF0000]/30 text-[#FF0000]/50 hover:text-[#FF0000] hover:border-[#FF0000]/60"
                    }`}
                    data-testid="atmos-toggle"
                    title="Dolby Atmos HRTF spatial audio — use headphones"
                  >
                    {atmosEnabled ? "ATMOS ON" : "ATMOS"}
                  </button>
                  <Button
                    onClick={() => buyViaStripe(ALBUM_PACKAGE_MAP[selectedAlbum.key] || `album_${selectedAlbum.key}`)}
                    disabled={buyingId === (ALBUM_PACKAGE_MAP[selectedAlbum.key] || `album_${selectedAlbum.key}`)}
                    className="bg-[#FF1744] text-black hover:bg-[#FF1744]/90 font-bold"
                    data-testid="buy-album-btn"
                  >
                    {buyingId === (ALBUM_PACKAGE_MAP[selectedAlbum.key] || `album_${selectedAlbum.key}`) ? (
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    ) : (
                      <ShoppingCart className="h-4 w-4 mr-2" />
                    )}
                    Buy Album — $13
                  </Button>

                  {selectedAlbum.key === "codes" && (
                    <Button
                      onClick={() => buyViaStripe("album_codes")}
                      disabled={buyingId === "album_codes"}
                      className="bg-[#FF1744]/80 text-black hover:bg-[#FF1744]"
                      data-testid="buy-codes-btn"
                    >
                      {buyingId === "album_codes" ? (
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      ) : (
                        <ShoppingCart className="h-4 w-4 mr-2" />
                      )}
                      Buy CODES — $13
                    </Button>
                  )}
                </div>
              </div>

              <Separator className="my-4 bg-[#FF1744]/20" />

              {selectedAlbum.tracks && selectedAlbum.tracks.length > 0 ? (
                <div data-testid={`${selectedAlbum.key}-tracklist`}>
                  <div className="flex items-center justify-between gap-3 flex-wrap">
                    <div className="text-sm font-semibold" style={{ color: "rgba(255, 23, 68, 0.90)" }}>
                      {selectedAlbum.title.toUpperCase()} — Full Tracks
                    </div>
                    <div className="text-xs" style={{ color: "rgba(255, 23, 68, 0.70)" }}>
                      {selectedAlbum.tracks.length} tracks • More coming soon
                    </div>
                  </div>

                  <div className="mt-3 space-y-2">
                    {selectedAlbum.tracks.map((t) => {
                      const isPlaying = playingTrackId === t.id && !isPaused;
                      const isThis = playingTrackId === t.id;
                      const pct = isThis && trackDuration > 0 ? (trackProgress / trackDuration) * 100 : 0;

                      return (
                        <div
                          key={t.id}
                          className="border px-3 py-3 space-y-2"
                          style={{ borderColor: isThis ? "rgba(255, 0, 0, 0.5)" : "rgba(255, 23, 68, 0.2)", background: isThis ? "rgba(255, 0, 0, 0.05)" : "rgba(7,0,2,0.55)" }}
                          data-testid={`mirrors-track-${t.id}`}
                        >
                          <div className="flex items-center justify-between gap-3">
                            <div className="min-w-0">
                              <div className="flex items-center gap-2">
                                <div className="text-xs w-6 font-mono" style={{ color: "rgba(255, 23, 68, 0.55)" }}>
                                  {t.track_number}
                                </div>
                                <div className="truncate text-sm font-bold" style={{ color: "#FF0000" }}>
                                  {t.title}
                                </div>
                              </div>
                              <div className="flex items-center gap-3 text-[10px] font-mono mt-0.5" style={{ color: "rgba(255, 23, 68, 0.55)" }}>
                                <span>Key: {t.camelot}</span>
                                <span>BPM: {t.bpm}</span>
                                {isThis && <span>{formatSec(trackProgress)} / {formatSec(trackDuration)}</span>}
                              </div>
                            </div>

                            <div className="flex items-center gap-2 shrink-0">
                              <Button
                                size="sm"
                                variant="outline"
                                className="border-[#FF0000]/50 text-[#FF0000] hover:bg-[#FF0000]/10"
                                onClick={() => playFullTrack(t)}
                                data-testid={`play-${t.id}`}
                              >
                                {isPlaying ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4" />}
                                <span className="ml-1.5 text-xs">{isPlaying ? "Pause" : "Play"}</span>
                              </Button>
                              <Button
                                size="sm"
                                className="bg-[#FF0000] text-black hover:bg-[#FF0000]/80 font-bold"
                                onClick={() => buyViaStripe(ALBUM_PACKAGE_MAP[selectedAlbum.key] || `album_${selectedAlbum.key}`)}
                                disabled={buyingId === (ALBUM_PACKAGE_MAP[selectedAlbum.key] || `album_${selectedAlbum.key}`)}
                                data-testid={`buy-${t.id}`}
                              >
                                {buyingId === (ALBUM_PACKAGE_MAP[selectedAlbum.key] || `album_${selectedAlbum.key}`) ? (
                                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                                ) : (
                                  <ShoppingCart className="h-3.5 w-3.5" />
                                )}
                                <span className="ml-1 text-xs">Buy $13</span>
                              </Button>
                            </div>
                          </div>

                          {/* Progress bar — clickable to seek */}
                          {isThis && (
                            <div
                              className="w-full h-1.5 bg-[#FF0000]/10 cursor-pointer"
                              onClick={(e) => seekTrack(e, t)}
                              data-testid={`progress-${t.id}`}
                            >
                              <div
                                className="h-full bg-[#FF0000] transition-none"
                                style={{ width: `${pct}%` }}
                              />
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>

                  <div className="mt-3 text-[10px] font-mono" style={{ color: "rgba(255, 23, 68, 0.5)" }}>
                    Full songs available for preview • Buy links connect to artist store
                  </div>
                </div>
              ) : selectedAlbum.itunesAlbumId ? (
                <div>
                  <div className="flex items-center justify-between gap-3 flex-wrap">
                    <div className="text-sm font-semibold" style={{ color: "rgba(255, 23, 68, 0.90)" }}>
                      {selectedAlbum.title.toUpperCase()} — Track Previews (30s)
                    </div>
                    <div className="text-xs" style={{ color: "rgba(255, 23, 68, 0.70)" }}>
                      Source: iTunes public lookup
                    </div>
                  </div>

                  {isLoadingTracks ? (
                    <div className="mt-4 text-sm" style={{ color: "rgba(255, 23, 68, 0.78)" }}>
                      Loading tracks…
                    </div>
                  ) : (
                    <div className="mt-3 space-y-2">
                      {itunesTracks.map((t) => {
                        const isPlaying = playingTrackId === t.track_id && !isPaused;
                        const canPlay = !!t.preview_url;

                        return (
                          <div
                            key={t.track_id}
                            className="flex items-center justify-between gap-3 rounded-md border px-3 py-2"
                            style={{ borderColor: "rgba(255, 23, 68, 0.25)", background: "rgba(7,0,2,0.55)" }}
                          >
                            <div className="min-w-0">
                              <div className="flex items-center gap-2">
                                <div className="text-xs w-6" style={{ color: "rgba(255, 23, 68, 0.65)" }}>
                                  {t.track_number}
                                </div>
                                <div className="truncate text-sm font-semibold" style={{ color: "#FF1744" }}>
                                  {t.title}
                                </div>
                              </div>
                              <div className="text-xs" style={{ color: "rgba(255, 23, 68, 0.70)" }}>
                                {formatMs(t.duration_ms)}
                              </div>
                            </div>

                            <div className="flex items-center gap-2 shrink-0">
                              <Button
                                size="sm"
                                variant="outline"
                                className={
                                  canPlay
                                    ? "border-[#FF1744]/60 text-[#FF1744] hover:bg-[#FF1744]/10"
                                    : "border-[#FF1744]/30 text-[#FF1744]/40 cursor-not-allowed"
                                }
                                onClick={() => playPreview(t)}
                                disabled={!canPlay}
                              >
                                {isPlaying ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4" />}
                                <span className="ml-2">{isPlaying ? "Pause" : "Play"}</span>
                              </Button>

                              <Button
                                size="sm"
                                className="bg-[#FF1744] text-black hover:bg-[#FF1744]/80 font-bold"
                                onClick={() => buyViaStripe(ALBUM_PACKAGE_MAP[selectedAlbum.key] || `album_${selectedAlbum.key}`)}
                                disabled={buyingId === (ALBUM_PACKAGE_MAP[selectedAlbum.key] || `album_${selectedAlbum.key}`)}
                                data-testid={`buy-itunes-${t.track_id}`}
                              >
                                {buyingId === (ALBUM_PACKAGE_MAP[selectedAlbum.key] || `album_${selectedAlbum.key}`) ? (
                                  <Loader2 className="h-4 w-4 animate-spin" />
                                ) : (
                                  <ShoppingCart className="h-4 w-4" />
                                )}
                                <span className="ml-1">Buy $13</span>
                              </Button>
                            </div>
                          </div>
                        );
                      })}

                      {itunesTracks.length === 0 && (
                        <div className="mt-4 text-sm" style={{ color: "rgba(255, 23, 68, 0.78)" }}>
                          No preview tracks found.
                        </div>
                      )}
                    </div>
                  )}

                  <div className="mt-4 text-xs" style={{ color: "rgba(255, 23, 68, 0.7)" }}>
                    Tip: previews are public 30s samples. MIRRORS ID unlocks the full album link.
                  </div>
                </div>
              ) : (
                <div className="text-sm" style={{ color: "rgba(255, 23, 68, 0.78)" }}>
                  Track previews for this record are being wired into the grid.
                  <div className="mt-2">For now, use the buy link above.</div>
                </div>
              )}
            </CardContent>
          </Card>
          </>
          )}

          <div className="text-xs" style={{ color: "rgba(255, 23, 68, 0.55)" }}>
            SOUND • GODMODE SOUND • VINCE SINNOTT
          </div>
        </div>
      </div>
    </div>
  );
}
